/**
 * What a screen shows instead of itself when there is no venue to show it
 * for — the first thing the owner of a brand-new organisation sees.
 *
 * The diagnosis is `venueGapNotice` in `@baros/core`, so every screen tells
 * the same story and the two cases stay apart: an organisation with no
 * venues at all is not the same problem as a person who has not been put on
 * one, and only the first is the reader's own to fix.
 *
 * Not an error. `msg` without `err`: an empty organisation is where every
 * customer starts, and painting it red says something broke.
 */

import { venueGapNotice } from "@baros/core";
import { useApp } from "./context.js";

/**
 * The venue this session may actually act at, or `null`.
 *
 * `useApp().venueId` no longer falls back to the org's first venue when
 * nothing is assigned — that fallback handed somebody a venue every read
 * then 403s on, and because App.tsx fetches it on boot the whole app came up
 * as an error page. This re-checks it against the membership anyway, because
 * a screen asking "may I act at a venue" should not depend on how one
 * `useMemo` in App.tsx happens to be written today. A `null` is the signal
 * to show `<NoVenue/>` and say which of the two problems it is.
 */
export function useActingVenue(): string | null {
  const { me, venueId } = useApp();
  if (!venueId) return null;
  return me.venues.some((v) => v.venueId === venueId && v.assigned) ? venueId : null;
}

interface Props {
  /**
   * What this screen would have shown, as a noun phrase: `"a prep list"`,
   * `"a stocktake"`. It lands mid-sentence, so no leading capital and no
   * full stop.
   */
  readonly what: string;
}

export function NoVenue({ what }: Props) {
  const { me, go } = useApp();
  const notice = venueGapNotice({
    venueCount: me.venues.length,
    canManageOrg: me.can["org:manage"],
    what,
  });

  return (
    <div className="pad">
      <div className="msg">
        <div style={{ fontWeight: 600 }}>{notice.title}</div>
        <div style={{ color: "var(--t2)", marginTop: 4 }}>{notice.detail}</div>
        {notice.action && (
          <button
            className="btn"
            style={{ marginTop: 14 }}
            onClick={() => go({ kind: "org" })}
          >
            {notice.action} →
          </button>
        )}
      </div>
    </div>
  );
}
